// src/components/VotingPanel.jsx
import { Btn } from "./ui.jsx";
import { PHASES, ROLES } from "../utils/constants.js";

export default function VotingPanel({
  state, me,
  onMafiaVote, onDoctorVote, onDayVote, onConfirmDayVote, onSkipVote,
}) {
  const phase     = state.phase;
  const players   = state.players || [];
  const isMafia   = me.role === ROLES.MAFIA;
  const isDoctor  = me.role === ROLES.DOCTOR;
  const isAlive   = me.alive;
  const skipped   = state.skippedVotes  || [];
  const confirmed = state.confirmedVotes || [];
  const iSkipped  = skipped.includes(me.id);
  const iConfirmed= confirmed.includes(me.id);

  const alivePlayers = players.filter((p) => p.alive);
  const myDayVote    = (state.dayVotes || {})[me.id];
  const targets      = alivePlayers.filter((p) => p.id !== me.id);

  // ── Day tally ──
  const dayTally = {};
  if (phase === PHASES.DAY) {
    alivePlayers.forEach((p) => {
      const t = (state.dayVotes || {})[p.id];
      if (t) dayTally[t] = (dayTally[t] || 0) + 1;
    });
  }

  // ══════════════════════════════════════════════════════════════
  // NIGHT — Mafia voting
  // ══════════════════════════════════════════════════════════════
  if (phase === PHASES.NIGHT && isMafia && isAlive) {
    const myVote = (state.mafiaVotes || {})[me.id];
    return (
      <div>
        <SectionHeader>Choose Your Target</SectionHeader>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.75rem" }}>
          {targets.map((p) => {
            const isVoted = myVote === p.id;
            const voterNames = Object.entries(state.mafiaVotes || {})
              .filter(([, t]) => t === p.id)
              .map(([v]) => players.find((x) => x.id === v)?.name || "?");
            return (
              <TargetRow
                key={p.id}
                name={p.name}
                selected={isVoted}
                selectedColor="rgba(139,26,26,0.22)"
                selectedBorder="var(--blood-bright)"
                defaultBorder="rgba(139,26,26,0.25)"
                onClick={() => onMafiaVote(p.id)}
                disabled={iSkipped}
              >
                {voterNames.length > 0 && (
                  <PipList pips={voterNames} color="#e05555" bg="rgba(139,26,26,0.3)" border="rgba(139,26,26,0.5)" />
                )}
                {isVoted && <Tag color="#e05555">✓ Chosen</Tag>}
              </TargetRow>
            );
          })}
        </div>
        <SkipBtn skipped={iSkipped} onSkip={onSkipVote} />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // SAVE — Doctor voting
  // ══════════════════════════════════════════════════════════════
  if (phase === PHASES.SAVE && isDoctor && isAlive) {
    const myVote = (state.doctorVotes || {})[me.id];
    return (
      <div>
        <SectionHeader>Choose Who to Save</SectionHeader>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.75rem" }}>
          {alivePlayers.map((p) => {
            const isVoted = myVote === p.id;
            return (
              <TargetRow
                key={p.id}
                name={`${p.name}${p.id === me.id ? " (you)" : ""}`}
                selected={isVoted}
                selectedColor="rgba(26,92,92,0.22)"
                selectedBorder="#2a9090"
                defaultBorder="rgba(26,92,92,0.28)"
                onClick={() => onDoctorVote(p.id)}
                disabled={iSkipped}
              >
                {isVoted && <Tag color="#4db8b8">💊 Saving</Tag>}
              </TargetRow>
            );
          })}
        </div>
        <SkipBtn skipped={iSkipped} onSkip={onSkipVote} />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // DAY — Town voting
  // ══════════════════════════════════════════════════════════════
  if (phase === PHASES.DAY && isAlive) {
    const done = iConfirmed || iSkipped;
    return (
      <div>
        <SectionHeader>
          {done
            ? iSkipped ? "You skipped this vote" : "✓ Vote locked in"
            : "Vote to eliminate a suspect"}
        </SectionHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.75rem" }}>
          {targets.map((p) => {
            const isMyVote  = myDayVote === p.id;
            const voteCount = dayTally[p.id] || 0;
            const allVoters = Object.entries(state.dayVotes || {})
              .filter(([, t]) => t === p.id)
              .map(([v]) => ({
                name:      players.find((x) => x.id === v)?.name || "?",
                confirmed: confirmed.includes(v),
              }));

            return (
              <div
                key={p.id}
                onClick={() => !done && onDayVote(p.id)}
                style={{
                  padding: "0.7rem 0.9rem",
                  border: `2px solid ${isMyVote ? "var(--gold)" : voteCount > 0 ? "rgba(201,168,76,0.25)" : "rgba(201,168,76,0.1)"}`,
                  borderRadius: 2,
                  background: isMyVote ? "rgba(201,168,76,0.1)" : "rgba(255,255,255,0.02)",
                  cursor: done ? "default" : "pointer",
                  transition: "all 0.2s",
                  animation: isMyVote ? "voteGlow 1.5s ease infinite" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <span style={{ flex: 1, fontSize: "0.88rem" }}>{p.name}</span>
                  {voteCount > 0 && (
                    <span style={{ fontSize: "0.75rem", color: "var(--gold)", fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      {voteCount} vote{voteCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {isMyVote && !done && <Tag color="var(--gold)">← Your vote</Tag>}
                </div>
                {allVoters.length > 0 && (
                  <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                    {allVoters.map((v, i) => (
                      <span key={i} style={{ fontSize: "0.58rem", padding: "0.1rem 0.3rem", borderRadius: 2, background: v.confirmed ? "rgba(201,168,76,0.2)" : "rgba(201,168,76,0.07)", border: `1px solid ${v.confirmed ? "rgba(201,168,76,0.45)" : "rgba(201,168,76,0.18)"}`, color: "var(--gold)" }}>
                        {v.name}{v.confirmed ? " ✓" : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Confirm / Skip row */}
        {!done && (
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <Btn
              variant="blood" sm
              disabled={!myDayVote}
              onClick={onConfirmDayVote}
              style={{ flex: 1, minWidth: 120 }}
            >
              ⚖ Lock In Vote
            </Btn>
            <Btn variant="dim" sm onClick={onSkipVote} style={{ flex: "0 0 auto" }}>
              Skip
            </Btn>
          </div>
        )}
        {done && !iSkipped && (
          <div style={{ color: "var(--gold-dim)", fontSize: "0.78rem", textAlign: "center", padding: "0.4rem" }}>
            ✓ Vote locked. Waiting for others...
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ── Sub-components ─────────────────────────────────────────────
function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "var(--gold-dim)", marginBottom: "0.65rem", textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

function TargetRow({ name, selected, selectedColor, selectedBorder, defaultBorder, onClick, disabled, children }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: "flex", alignItems: "center", gap: "0.6rem",
        padding: "0.65rem 0.9rem",
        border: `1px solid ${selected ? selectedBorder : defaultBorder}`,
        borderRadius: 2,
        background: selected ? selectedColor : "rgba(255,255,255,0.02)",
        cursor: disabled ? "default" : "pointer",
        transition: "all 0.18s",
        animation: selected ? "voteGlow 1.5s ease infinite" : "none",
      }}
    >
      <span style={{ flex: 1, fontSize: "0.88rem" }}>{name}</span>
      {children}
    </div>
  );
}

function Tag({ color, children }) {
  return (
    <span style={{ fontSize: "0.7rem", color, whiteSpace: "nowrap" }}>{children}</span>
  );
}

function PipList({ pips, color, bg, border }) {
  return (
    <div style={{ display: "flex", gap: "0.2rem", flexWrap: "wrap" }}>
      {pips.map((n, i) => (
        <span key={i} style={{ fontSize: "0.58rem", padding: "0.1rem 0.3rem", borderRadius: 2, background: bg, border: `1px solid ${border}`, color }}>
          {n}
        </span>
      ))}
    </div>
  );
}

function SkipBtn({ skipped, onSkip }) {
  return skipped ? (
    <div style={{ fontSize: "0.75rem", color: "rgba(242,234,216,0.35)", fontStyle: "italic" }}>
      You skipped your vote this round.
    </div>
  ) : (
    <Btn variant="dim" sm onClick={onSkip}>Skip Vote</Btn>
  );
}
